import {AxiosResponse} from 'axios'
import {unwrapAPIError} from 'utils'

import {
  ShadowITRequest,
  ShadowITResponse,
  ProductivityResponse,
  PolicyViolationTransactionsResponse,
  OverviewViolationsState,
  OverviewAnalyticsState,
  TopTenBlocksResponseBody,
  TopViolationsByTypeResponseBody,
  GetTopTenBlocksAction,
  AnalyticsDetailAPIResponseBody,
  GetAnalyticsDetailRequestPayload,
  OverviewEndpointsState,
  ProductivityRequest,
  AnalyticsSearchQueryParams,
  GetAnalyticsDetailRequestParams,
  UnknownObject,
  ActivityByTimeResponse,
  ActivityByTimeRequest,
  ACTIVITY_BY_TIME,
  AnalyticsPolicy7DayTrendState,
  GetAnalyticsUserVolume,
  GetShadowITAppVolumes,
  ShadowITOverviewVolumesResponse,
  RecategorizationStatusResponse,
  DLPDopamineResponse,
} from 'interfaces'

import {
  requestHandler,
  getAnalyticsBaseUrl,
  getViolationsBaseUrl,
  getPBFFServiceBaseUrl,
  getFlightdeckBaseUrl,
} from '../HTTP'
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types'
import {secs35} from 'definitions/constant'
const analyticsBaseUrl = getAnalyticsBaseUrl()
const violationsBaseUrl = getViolationsBaseUrl()
const analyticsOverviewBaseUrl = getPBFFServiceBaseUrl()
const flightdeckBaseUrl = getFlightdeckBaseUrl()

const getShadowIT = async ({
  params,
}: ShadowITRequest): Promise<AxiosResponse<ShadowITResponse[]>> => {
  try {
    return await requestHandler<ShadowITResponse[]>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: analyticsBaseUrl + EndPoints.ShadowITApplication,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

// Productivity
const getProductivity = async ({
  params,
}: ProductivityRequest): Promise<AxiosResponse<ProductivityResponse>> => {
  try {
    return await requestHandler<ProductivityResponse>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: analyticsBaseUrl + EndPoints[`GetProductivity`],
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

// Productivity
const getActivityByTime = async ({
  activityByTimeFormat,
  params,
}: ActivityByTimeRequest): Promise<AxiosResponse<ActivityByTimeResponse>> => {
  const endpoint = EndPoints.GetActivityByTime
  try {
    return await requestHandler<ActivityByTimeResponse>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${endpoint}?day=${
        activityByTimeFormat === ACTIVITY_BY_TIME.Day
      }`,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getTopTenBlocks = async (
  payload: GetTopTenBlocksAction,
): Promise<AxiosResponse<TopTenBlocksResponseBody>> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestPayload: any = {
      method: HttpMethods.POST,
      // Override axios baseUrl
      url: `${violationsBaseUrl}${EndPoints.GetTopTenBlocks}`,
    }
    const filter = payload?.filter
    if (filter && Object.keys(filter).length) {
      requestPayload.data = {filter}
    }
    return await requestHandler<TopTenBlocksResponseBody>(requestPayload)
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getTopViolationsByType = async (): Promise<
  AxiosResponse<TopViolationsByTypeResponseBody>
> => {
  try {
    return await requestHandler<TopViolationsByTypeResponseBody>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${violationsBaseUrl}${EndPoints.GetTopViolationsByType}`,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getPolicyViolationTransactionSummary = async (): Promise<
  AxiosResponse<PolicyViolationTransactionsResponse>
> => {
  try {
    return await requestHandler<PolicyViolationTransactionsResponse>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${EndPoints.GetViolations}`,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const get7DayTrend = async (): Promise<AnalyticsPolicy7DayTrendState> => {
  return Promise.all([
    getTopViolationsByType(),
    getPolicyViolationTransactionSummary(),
  ])
    .then(([summaryResp, transactionResp]) => {
      const summaryData = summaryResp?.data?.response || {}
      const transactionData = transactionResp?.data || {}
      return {
        policy: {
          totalViolations: summaryData.policy?.total,
          details: summaryData.policy?.details,
          ...transactionData.policy,
        },
        malware: {
          totalBlocks: summaryData.malware?.total,
          details: summaryData.malware?.details,
          ...transactionData.malware,
        },
        cloudAppControl: {
          totalBlocks: summaryData.cac?.total,
          details: summaryData.cac?.details,
          ...transactionData.cloudAppControl,
        },
        dlp: {
          totalViolations: summaryData.dlp?.total,
          details: summaryData.dlp?.details,
        },
        a7DayTrendBlocks: transactionData.a7DayTrendBlocks,
      }
    })
    .catch(error => error)
}

const getOverviewEndpoints = async (): Promise<
  AxiosResponse<OverviewEndpointsState>
> => {
  try {
    return await requestHandler<OverviewEndpointsState>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsOverviewBaseUrl}${EndPoints.GetAnalyticsOverview}`,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getOverviewViolations = async (
  params?: AnalyticsSearchQueryParams,
): Promise<AxiosResponse<OverviewViolationsState>> => {
  try {
    return await requestHandler<OverviewViolationsState>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${violationsBaseUrl}${EndPoints.GetViolationsOverview}`,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getOverviewAnalytics = async (
  params?: AnalyticsSearchQueryParams,
): Promise<AxiosResponse<OverviewAnalyticsState>> => {
  try {
    return await requestHandler<OverviewAnalyticsState>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${EndPoints.GetAnalyticsOverview}`,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getAnalyticsDetail = async (
  reqPayload: GetAnalyticsDetailRequestPayload,
  params?: GetAnalyticsDetailRequestParams,
): Promise<AxiosResponse<AnalyticsDetailAPIResponseBody>> => {
  try {
    return await requestHandler<AnalyticsDetailAPIResponseBody>({
      method: HttpMethods.POST,
      // Override axios baseUrl
      url: `${violationsBaseUrl}${EndPoints.GetAnalyticsDetail}`,
      options: {
        params,
        timeout: secs35,
      },
      data: reqPayload as UnknownObject,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getAnalyticsUserVolumes = async (
  email: string,
): Promise<AxiosResponse<GetAnalyticsUserVolume[]>> => {
  try {
    return await requestHandler<GetAnalyticsUserVolume[]>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${EndPoints.ShadowITUsers}/${email}/volumes`,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getAnalyticsAppVolumes = async (
  appName: string,
  endCursor: string,
  searchType?: string,
  searchTerm?: string,
  first: number = 50,
): Promise<AxiosResponse<GetShadowITAppVolumes>> => {
  const params = {
    after: endCursor,
    first,
  }
  if (searchType) {
    params[searchType] = searchTerm
  }
  try {
    return await requestHandler<GetShadowITAppVolumes>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${EndPoints.ShadowITApplication}/${appName}/volumes`,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getShadowITOverviewVolumes = async (
  searchType?: string,
  searchTerm?: string,
): Promise<AxiosResponse<ShadowITOverviewVolumesResponse>> => {
  const params = {}
  if (searchType) {
    params[searchType] = searchTerm
  }
  try {
    return await requestHandler<ShadowITOverviewVolumesResponse>({
      method: HttpMethods.GET,
      // Override axios baseUrl
      url: `${analyticsBaseUrl}${EndPoints.ShadowITApplication}/overview`,
      options: {
        params,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

/**
 * Get recategorization status for a URL
 *
 * @param domain Domain of the URL
 * @param url URL to check status for
 * @returns AxiosResponse with recategorization status data
 */
const getRecategorizationStatus = async (
  url: string,
): Promise<AxiosResponse<RecategorizationStatusResponse>> => {
  try {
    return await requestHandler<RecategorizationStatusResponse>({
      method: HttpMethods.GET,
      url: flightdeckBaseUrl + `/categories${EndPoints.RecategorizationStatus}`,
      options: {
        params: {url},
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(new Error(errorValue))
  }
}

const getDLPDopamine = async (
  fileHash: string,
): Promise<DLPDopamineResponse> => {
  try {
    const {data} = await requestHandler<DLPDopamineResponse>({
      method: HttpMethods.GET,
      url: `${violationsBaseUrl}${EndPoints.DLPDopamine.replace(
        '{fileHash}',
        fileHash,
      )}`,
    })
    return data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    throw errorValue
  }
}

export const AnalyticsService = {
  getShadowIT,
  getProductivity,
  getActivityByTime,
  getTopTenBlocks,
  get7DayTrend,
  getTopViolationsByType,
  getPolicyViolationTransactionSummary,
  getOverviewEndpoints,
  getOverviewViolations,
  getOverviewAnalytics,
  getAnalyticsDetail,
  getAnalyticsUserVolumes,
  getAnalyticsAppVolumes,
  getShadowITOverviewVolumes,
  getRecategorizationStatus,
  getDLPDopamine,
}
